import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { KavachSwitch, KavachStats, StationStats } from './kavachTypes';

/**
 * Format date for PDF header
 */
const formatDate = (): string => {
  return new Date().toLocaleString('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Asia/Kolkata',
  });
};

/**
 * Add header to PDF
 */
const addHeader = (doc: jsPDF, title: string): void => {
  const pageWidth = doc.internal.pageSize.getWidth();

  // Title
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text(title, pageWidth / 2, 20, { align: 'center' });

  // Subtitle with date
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Generated on: ${formatDate()}`, pageWidth / 2, 28, { align: 'center' });

  // Line separator
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.5);
  doc.line(14, 32, pageWidth - 14, 32);
};

/**
 * Add footer to PDF
 */
const addFooter = (doc: jsPDF): void => {
  const pageCount = doc.getNumberOfPages();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
    doc.text('KAVACH Network Switch Monitoring System', 14, pageHeight - 10);
  }
};

/**
 * Export all switches to PDF
 */
export const exportSwitchesToPdf = (
  switches: KavachSwitch[],
  stats: KavachStats,
  stationStats: StationStats[],
  filename: string = 'kavach-switches-report'
): void => {
  const doc = new jsPDF('landscape', 'mm', 'a4');

  addHeader(doc, 'KAVACH Network Switches Report');

  // Summary stats
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Summary', 14, 40);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  const summaryData = [
    `Total Switches: ${stats.totalSwitches}`,
    `Ports Up: ${stats.portsUp}`,
    `Ports Down: ${stats.portsDown}`,
    `Active Alerts: ${stats.activeAlerts}`,
  ];
  doc.text(summaryData, 14, 48);

  // Switches table
  const tableData = switches.map((sw) => [
    sw.lastActivity,
    sw.name,
    sw.deviceType || 'Media Converter',
    sw.station,
    sw.ports.port1.status,
    sw.ports.port2.status,
    sw.ports.port3.status,
    sw.ports.port4.status,
    `${Object.values(sw.ports).filter(p => p.status === 'UP').length}/4 Up`,
  ]);

  autoTable(doc, {
    head: [['Last Activity', 'Device Name', 'Device Type', 'Station', 'Port 1', 'Port 2', 'Port 3', 'Port 4', 'Overall']],
    body: tableData,
    startY: 65,
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [26, 82, 118], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [245, 245, 245] },
    columnStyles: {
      4: { halign: 'center' },
      5: { halign: 'center' },
      6: { halign: 'center' },
      7: { halign: 'center' },
      8: { halign: 'center' },
    },
    didParseCell: (data) => {
      // Color port status cells
      if (data.section === 'body' && data.column.index >= 4 && data.column.index <= 7) {
        const value = data.cell.raw as string;
        if (value === 'UP') {
          data.cell.styles.textColor = [39, 174, 96];
          data.cell.styles.fontStyle = 'bold';
        } else if (value === 'DOWN') {
          data.cell.styles.textColor = [231, 76, 60];
          data.cell.styles.fontStyle = 'bold';
        }
      }
    },
  });

  // Station-wise summary on new page
  if (stationStats.length > 0) {
    doc.addPage();
    addHeader(doc, 'Station-wise Summary');

    const stationData = stationStats.map((s) => [
      s.station,
      s.totalSwitches.toString(),
      s.portsUp.toString(),
      s.portsDown.toString(),
      `${((s.portsUp / (s.portsUp + s.portsDown)) * 100).toFixed(1)}%`,
    ]);

    autoTable(doc, {
      head: [['Station', 'Total Switches', 'Ports Up', 'Ports Down', 'Availability']],
      body: stationData,
      startY: 40,
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [26, 82, 118], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 245, 245] },
    });
  }

  addFooter(doc);
  doc.save(`${filename}.pdf`);
};

/**
 * Export single switch details to PDF
 */
export const exportSwitchDetailToPdf = (sw: KavachSwitch): void => {
  const doc = new jsPDF('portrait', 'mm', 'a4');

  addHeader(doc, `KAVACH Switch Details - ${sw.name}`);

  let yPos = 45;

  // Device Info Section
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Device Information', 14, yPos);
  yPos += 8;

  const deviceInfo = [
    ['Device Name', sw.name],
    ['Device Type', sw.deviceType || 'Media Converter'],
    ['Station', sw.station],
    ['Last Activity', sw.lastActivity],
  ];

  autoTable(doc, {
    body: deviceInfo,
    startY: yPos,
    styles: { fontSize: 10, cellPadding: 3 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 50 },
      1: { cellWidth: 100 },
    },
    theme: 'plain',
  });

  yPos = (doc as any).lastAutoTable.finalY + 10;

  // Port Status Section
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Port Status', 14, yPos);
  yPos += 8;

  const portData = [
    ['Port 1', sw.ports.port1.status, sw.ports.port1.speed],
    ['Port 2', sw.ports.port2.status, sw.ports.port2.speed],
    ['Port 3', sw.ports.port3.status, sw.ports.port3.speed],
    ['Port 4', sw.ports.port4.status, sw.ports.port4.speed],
  ];

  autoTable(doc, {
    head: [['Port', 'Status', 'Speed']],
    body: portData,
    startY: yPos,
    styles: { fontSize: 10, cellPadding: 3 },
    headStyles: { fillColor: [26, 82, 118], textColor: 255, fontStyle: 'bold' },
    didParseCell: (data) => {
      if (data.section === 'body' && data.column.index === 1) {
        const value = data.cell.raw as string;
        if (value === 'UP') {
          data.cell.styles.textColor = [39, 174, 96];
          data.cell.styles.fontStyle = 'bold';
        } else {
          data.cell.styles.textColor = [231, 76, 60];
          data.cell.styles.fontStyle = 'bold';
        }
      }
    },
  });

  yPos = (doc as any).lastAutoTable.finalY + 10;

  // Asset Details Section
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Asset Details', 14, yPos);
  yPos += 8;

  const { assetDetails } = sw;
  const assetData = [
    ['Make', assetDetails.make || 'N/A'],
    ['Model', assetDetails.model || 'N/A'],
    ['Serial Number', assetDetails.serialNumber || 'N/A'],
    ['MAC Address', assetDetails.macAddress || 'N/A'],
    ['IP Address', assetDetails.ipAddress || 'N/A'],
    ['Firmware Version', assetDetails.firmwareVersion || 'N/A'],
    ['Purchase Date', assetDetails.purchaseDate || 'N/A'],
    ['Installation Date', assetDetails.installationDate || 'N/A'],
    ['Warranty Expiry', assetDetails.warrantyExpiry || 'N/A'],
    ['Warranty Status', assetDetails.warrantyStatus || 'N/A'],
    ['Codal Life', assetDetails.codalLife || 'N/A'],
    ['Latitude', assetDetails.latitude || 'N/A'],
    ['Longitude', assetDetails.longitude || 'N/A'],
    ['Platform No', assetDetails.platformNo || 'N/A'],
  ];

  autoTable(doc, {
    body: assetData,
    startY: yPos,
    styles: { fontSize: 9, cellPadding: 2 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 50 },
      1: { cellWidth: 100 },
    },
    theme: 'striped',
  });

  addFooter(doc);
  doc.save(`kavach-switch-${sw.name.replace(/[^a-zA-Z0-9]/g, '-')}.pdf`);
};
